import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Divider, message, Modal, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type UserlikeChatbotModuleSpecsFragment } from '../../../../api/fragments/UserlikeChatbotModuleSpecs';
import {
    DeleteUserlikeChatbotSettingsDocument,
    type DeleteUserlikeChatbotSettingsMutation,
    type DeleteUserlikeChatbotSettingsMutationVariables,
} from '../../../../api/mutations/deleteUserlikeChatbotSettings';

export type UserlikeChatbotSettingActionsProps = {
    module: UserlikeChatbotModuleSpecsFragment;
    settings: UserlikeChatbotModuleSpecsFragment['chatbotSettings'][number];
};

const UserlikeChatbotSettingActions = ({ module, settings }: UserlikeChatbotSettingActionsProps) => {
    const moduleId = module.id;
    const settingsId = settings.id;

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { t } = useTranslation(['userlikeChatbotModuleDetails']);

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('userlikeChatbotModuleDetails:settings.deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('userlikeChatbotModuleDetails:settings.deleteModal.content'),
            okText: t('userlikeChatbotModuleDetails:settings.deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('userlikeChatbotModuleDetails:settings.deleteModal.cancelButton'),
            onOk: async () => {
                message.loading({
                    content: t('userlikeChatbotModuleDetails:settings.deleteModal.messages.submitting'),
                    key: 'primary',
                    duration: 0,
                });

                await apolloClient
                    .mutate<DeleteUserlikeChatbotSettingsMutation, DeleteUserlikeChatbotSettingsMutationVariables>({
                        mutation: DeleteUserlikeChatbotSettingsDocument,
                        variables: { moduleId, id: settingsId },
                    })
                    .finally(() => {
                        message.destroy('primary');
                    });

                message.success({
                    content: t('userlikeChatbotModuleDetails:settings.deleteModal.messages.success'),
                    key: 'primary',
                });
            },
        });
    }, [apolloClient, moduleId, settingsId, t]);

    return (
        <Space size="small" split={<Divider type="vertical" />}>
            <Button
                onClick={() => navigate(`/admin/system/modules/${moduleId}/userlike/${settingsId}`)}
                size="small"
                style={{ padding: 0 }}
                type="link"
            >
                {t('userlikeChatbotModuleDetails:settingsTable.actions.update')}
            </Button>
            <Button onClick={deleteAction} size="small" style={{ padding: 0 }} type="link">
                {t('userlikeChatbotModuleDetails:settingsTable.actions.delete')}
            </Button>
        </Space>
    );
};

export default UserlikeChatbotSettingActions;
